
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useCreateEmployeeMutation } from "../../../services/profileService";
import { styled } from "@mui/system";

const CreateEmployee = () => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [createEmployee, { isLoading }] = useCreateEmployeeMutation();
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");

  const handleToastClose = () => {
    setToastOpen(false);
  };

  // Toast function for reusability
  const showToast = (message, severity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  const onSubmit = async (data) => {
    try {
      await createEmployee(data).unwrap();
      showToast("Employee added and email sent successfully!", "success");
      setTimeout(() => navigate("/manager-dashboard"), 2000);
      // alert("Employee added and email sent successfully!");
      // navigate("/manager-dashboard");
    } catch (error) {
      console.error("Error creating employee:", error);
      // alert(error?.message)

      showToast(error?.message || "This email is already registered. Please use another email.", "warning");
    }
  };

  return (
    <>
      <SectionWrap>
        <ContainerWrap>
          <RowWrap>
            <Col12Wrap sx={{ textAlign: "center", marginBottom: "20px" }}>
              <Typography
                sx={{
                  fontSize: "25px",
                  textAlign: "center",
                  fontWeight: "500",
                  margin: "0 10px",
                  color: "#2C2C2E",
                }}
              >
                Add New Employee
              </Typography>
            </Col12Wrap>
            <Col12Wrap>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormMiddleBoxWrap>
                  <RowWrap>
                    <Col6Wrap>
                      <FormGroupWrap>
                        <FormLabelWrap>Name</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          {...register("name", {
                            required: "Name is required",
                          })}
                          placeholder="Employee Name"
                        />
                        {errors.name && (
                          <ErrorText>{errors.name.message}</ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col6Wrap>
                    <Col6Wrap>
                      <FormGroupWrap>
                        <FormLabelWrap>Email</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          {...register("email", {
                            required: "Email is required",
                            pattern: {
                              value:
                                /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                              message: "Invalid email format",
                            },
                          })}
                          placeholder="Employee Email"
                        />
                        {errors.email && (
                          <ErrorText>{errors.email.message}</ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col6Wrap>
                    <Col12Wrap>
                      <FormGroupWrap sx={{ marginBottom: "0" }}>
                        <FormLabelWrap>Job Title</FormLabelWrap>
                        <CustomTextField
                          fullWidth
                          variant="outlined"
                          {...register("job_title", {
                            required: "Job Title is required",
                          })}
                          placeholder="Job Title"
                        />
                        {errors.job_title && (
                          <ErrorText>{errors.job_title.message}</ErrorText>
                        )}
                      </FormGroupWrap>
                    </Col12Wrap>
                  </RowWrap>
                </FormMiddleBoxWrap>
                <FormBtnBoxWrap>
                  <RowWrap>
                    <Col12Wrap sx={{ textAlign: "center" }}>
                      <CustomBtn
                        type="submit"
                        variant="outlined"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          "Add Employee"
                        )}
                      </CustomBtn>
                    </Col12Wrap>
                  </RowWrap>
                </FormBtnBoxWrap>
              </form>
            </Col12Wrap>
          </RowWrap>
        </ContainerWrap>
      </SectionWrap>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleToastClose}
          severity={toastSeverity}
          variant="filled"
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateEmployee;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0 100px",
}));
const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "1170px",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));
const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const FormMiddleBoxWrap = styled(Box)(() => ({
  padding: "50px 150px",
  background: "#ffffff",
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: "16px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
    paddingLeft: "100px",
    paddingRight: "100px",
  },
  "@media (max-width: 991px)": {
    paddingLeft: "50px",
    paddingRight: "50px",
  },
  "@media (max-width: 575px)": {
    paddingLeft: "15px",
    paddingRight: "15px",
  },
}));
const Col6Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "50%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  "@media (max-width: 767px)": {
    width: "100%",
  },
}));
const FormGroupWrap = styled(Box)(() => ({
  marginBottom: "30px",
  position: "relative",
}));
const CustomTextField = styled(TextField)(() => ({
  width: "99%",
  border: "1px solid #DADADA",
  borderRadius: "7px",
  "& .MuiInputBase-input": {
    fontSize: "16px",
    fontWeight: "300",
    lineHeight: "1",
    color: "#2C2C2E",
    padding: "14px 20px",
  },
  "& fieldset": {
    border: "none",
  },
}));
const ErrorText = styled(Typography)(() => ({
  fontSize: "12px",
  fontWeight: "400",
  lineHeight: "1.445",
  color: "#ff0000",
  marginTop: "5px",
}));
const FormBtnBoxWrap = styled(Box)(() => ({
  maxWidth: "1030px",
  marginLeft: "auto",
  marginRight: "auto",
  marginTop: "30px",
  "@media (max-width: 1199px)": {
    maxWidth: "100%",
  },
}));
const CustomBtn = styled(Button)(() => ({
  width: "100%",
  fontSize: "20px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "10px",
  padding: "15px 15px",
  maxWidth: "250px",
  boxShadow: "none !important",
}));
const FormLabelWrap = styled(Typography)(() => ({
  fontSize: "18px",
  fontWeight: "400",
  lineHeight: "1.445",
  color: "#2C2C2E",
  marginBottom: "5px",
}));
